import React from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, Dimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const CARD_WIDTH = 300;
const CARD_MARGIN = 12;

interface Event {
  id: string;
  symbol: string;
  event_type: string;
  event_date: string;
  description: string;
  impact: string;
  created_at: string;
}

interface UpcomingEventsSnippetsProps {
  events: Event[];
  onCardPress: (symbol: string, event: Event) => void;
  onArticlePress: (symbol: string, event: Event) => void;
}

/**
 * Get countdown text for event
 */
const getCountdown = (eventDate: string): string => {
  const now = new Date();
  const event = new Date(eventDate);
  const diffTime = event.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'Past';
  } else if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays <= 7) {
    return `${diffDays} days`;
  } else if (diffDays <= 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  } else {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  }
};

/**
 * Get icon for event type
 */
const getEventIcon = (eventType: string): keyof typeof Ionicons.glyphMap => {
  const type = eventType.toLowerCase();
  if (type.includes('earnings')) return 'bar-chart';
  if (type.includes('fda')) return 'medical';
  if (type.includes('product')) return 'rocket';
  if (type.includes('regulatory')) return 'document-text';
  if (type.includes('merger') || type.includes('acquisition')) return 'git-merge';
  if (type.includes('split')) return 'git-branch';
  if (type.includes('dividend')) return 'cash';
  return 'calendar';
};

/**
 * Get gradient colors for event type
 */
const getEventColors = (eventType: string): [string, string] => {
  const type = eventType.toLowerCase();
  if (type.includes('earnings')) return ['#6366F1', '#8B5CF6'];
  if (type.includes('fda')) return ['#10B981', '#059669'];
  if (type.includes('product')) return ['#F59E0B', '#EF4444'];
  if (type.includes('regulatory')) return ['#3B82F6', '#2563EB'];
  if (type.includes('merger') || type.includes('acquisition')) return ['#8B5CF6', '#A78BFA'];
  if (type.includes('split')) return ['#EC4899', '#F472B6'];
  if (type.includes('dividend')) return ['#14B8A6', '#0D9488'];
  return ['#6366F1', '#8B5CF6']; // default purple gradient
};

/**
 * Individual event card component
 */
const EventCard: React.FC<{
  event: Event;
  onCardPress: () => void;
  onArticlePress: () => void;
}> = ({ event, onCardPress, onArticlePress }) => {
  const countdown = getCountdown(event.event_date);
  const icon = getEventIcon(event.event_type);
  const [color1, color2] = getEventColors(event.event_type);

  return (
    <Pressable onPress={onCardPress} style={styles.cardPressable}>
      <LinearGradient
        colors={[color1, color2]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.card}
      >
        {/* Header with symbol badge and countdown */}
        <View style={styles.cardHeader}>
          <View style={styles.symbolBadge}>
            <Text style={styles.symbolText}>{event.symbol}</Text>
          </View>
          <View style={styles.countdownBadge}>
            <Text style={styles.countdownText}>{countdown}</Text>
          </View>
        </View>

        {/* Event icon and type */}
        <View style={styles.eventInfo}>
          <Ionicons name={icon} size={32} color="#FFFFFF" style={styles.eventIcon} />
          <Text style={styles.eventType} numberOfLines={1}>
            {event.event_type}
          </Text>
        </View>

        {/* Event description */}
        <Text style={styles.eventDescription} numberOfLines={2}>
          {event.description}
        </Text>

        {/* Footer with date and arrow button */}
        <View style={styles.cardFooter}>
          <Text style={styles.eventDate}>
            {new Date(event.event_date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Text>
          <Pressable
            onPress={e => {
              e.stopPropagation();
              onArticlePress();
            }}
            style={styles.articleButton}
          >
            <Ionicons name="arrow-forward" size={18} color="#FFFFFF" />
          </Pressable>
        </View>
      </LinearGradient>
    </Pressable>
  );
};

/**
 * Main component: Horizontal scrollable event cards
 */
export const UpcomingEventsSnippets: React.FC<UpcomingEventsSnippetsProps> = ({
  events,
  onCardPress,
  onArticlePress,
}) => {
  if (!events || events.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        snapToInterval={CARD_WIDTH + CARD_MARGIN}
        decelerationRate="fast"
        pagingEnabled={false}
      >
        {events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            onCardPress={() => onCardPress(event.symbol, event)}
            onArticlePress={() => onArticlePress(event.symbol, event)}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  scrollContent: {
    paddingHorizontal: 16,
    gap: CARD_MARGIN,
  },
  cardPressable: {
    width: CARD_WIDTH,
  },
  card: {
    width: CARD_WIDTH,
    borderRadius: 16,
    padding: 16,
    minHeight: 180,
    justifyContent: 'space-between',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  symbolBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  symbolText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  countdownBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  countdownText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  eventInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  eventIcon: {
    marginRight: 8,
  },
  eventType: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    flex: 1,
  },
  eventDescription: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  eventDate: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 12,
    fontWeight: '500',
  },
  articleButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
